import { useTranslation } from 'react-i18next';
import { Dispatch, MouseEvent, SetStateAction, useEffect, useState } from 'react';
import { Button, InfoMessage, Input, InputTextArea, Label, Modal, ModalInner, RadioSet, SelectBox, Text } from 'components';
import { SpaceCraftDetailData } from './types';
import {
  manageModalDetailListDownlink,           // View Detail용
  manageModalDetailListUplink,
  registerModulationList,
  registerPolarizationList,
  reedSolomonCodeList,
  registerConvolutionalCodeList,
  registerPCMCodingList,
  registerUplinkPCMCodeList,
  registerOperatingModeList,
} from './utils';
import axios from 'common/AxiosAuth';
import DetailDeleteModal from './DetailDeleteModal';
import { InformationCircleOutlineIcon, XOutlineIcon } from 'assets/svgs';
import { Message } from 'types/message';

interface ListElementProps {
  title: string;
  value: string | number;
}

interface ModalProps {
  data: SpaceCraftDetailData | null;
  setData: Dispatch<SetStateAction<SpaceCraftDetailData | null>>;
  onDelete?: (e: MouseEvent<HTMLButtonElement>) => void;
  onEdit?: (e: MouseEvent<HTMLButtonElement>) => void;
  onSpaceCraftUpdated: () => Promise<void>;
}


const SpaceCraftModalListElement = ({ title, value }: ListElementProps) => {
  return (
    <li className="border-primary rounded-lg border px-3 py-2">
      <Text weight="regular" size="body2" color="tertiary">
        {title}
      </Text>
      <Text weight="regular" size="body2" color="primary">
        {value}
      </Text>
    </li>
  );
};

const SpaceCraftModal = ({ data, setData, onSpaceCraftUpdated }: ModalProps) => {
  const { t } = useTranslation();
  const [isEdit, setIsEdit] = useState(false);
  const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false);
  const [name, setName] = useState<string>('');
  const [tleTitle, setTleTitle] = useState<string>('');
  const [tleFirst, setTleFirst] = useState<string>('');
  const [tleSecond, setTleSecond] = useState<string>('');
  const [centerFrequency, setCenterFreq] = useState<number>(0);
  const [modulationId, setModulation] = useState<number>(registerModulationList[0].value);
  const [polarizationId, setPolarization] = useState<number>(registerPolarizationList[0].value);
  const [rsCodeId, setReedSolomonCode] = useState<number>(reedSolomonCodeList[0].value);
  const [subFrequency, setSubcarrierFrequency] = useState<number>(0);
  const [convolutionalCodeId, setConvolutionalCode] = useState<number>(registerConvolutionalCodeList[0].value);
  const [pcmCodingId, setPCMCoding] = useState<number>(registerPCMCodingList[0].value);
  const [frameSyncWord, setFrameSyncWord] = useState<string>('');
  const [frameSize, setFrameSize] = useState<number>(0);
  const [syn, setSyn] = useState<number>(2);
  const [ctl, setCtl] = useState<number>(1);
  const [lts, setLts] = useState<number>(3);
  const [slip, setSlip] = useState<number>(0);
  const [other, setOther] = useState<string>('');
  const [bitRate, setBitRate] = useState<number>(0);
  const [pcmCode, setPcmCode] = useState<number>(registerUplinkPCMCodeList[0].value);
  const [idlePattern, setIdlePattern] = useState<string>('');
  const [idlePatternLength, setIdlePatternLength] = useState<number>(0);
  const [preambleLength, setPreambleLength] = useState<number>(0);
  const [carrierFrequencyMhz, setCarrierFrequencyMhz] = useState<number>(0);
  const [operatingMode, setOperatingMode] = useState<number>(registerOperatingModeList[0].value);
  const [ifCarrierLevel, setIfCarrierLevel] = useState<number>(0);
  const [sweepRate, setSweepRate] = useState<number>(0);
  const [sweepRange, setSweepRange] = useState<number>(0);
  const [messageArray, setMessageArray] = useState<Message[]>([]);
  const [messageArrayCount, setMessageArrayCount] = useState(0);
  useEffect(() => {
    console.log('Data loaded:', data);
  }, [data]);

  const openDeleteModal = () => {
    setIsDeleteModalOpened(true);
  };
  const onEdit = () => {
    setIsEdit(true);
  };

  const handleGoBack = () => {
    setIsEdit(false);
  };

  function onClickDeleteButton(index: number) {
    setMessageArray((prev) => prev.filter((message: any) => message.index !== index));
  }


  async function onSave() {
    const isUplink = data?.direction === 'UPLINK';

    let params: Record<string, unknown>;

    if (isUplink) {
      const polarizationLabel =
        registerPolarizationList.find((item) => item.value === polarizationId)?.label || 'LHCP';
      const pcmCodeLabel =
        registerUplinkPCMCodeList.find((item) => item.value === pcmCode)?.label || '';
      const operatingModeLabel =
        registerOperatingModeList.find((item) => item.value === operatingMode)?.label || '';

      params = {
        name,
        noradId: data?.noradId,
        tleTitle,
        tleFirst,
        tleSecond,
        polarization: polarizationLabel,
        bitRate: Number(bitRate),
        pcmCode: pcmCodeLabel,
        idlePattern,
        idlePatternLength: Number(idlePatternLength),
        preambleLength: Number(preambleLength),
        carrierFrequencyMhz: Number(carrierFrequencyMhz),
        operatingMode: operatingModeLabel,
        ifCarrierLevel: Number(ifCarrierLevel),
        sweepRate: Number(sweepRate),
        sweepRange: Number(sweepRange),
        other,
      };
    } else {
      params = {
        name,
        noradId: data?.noradId,
        tleTitle,
        tleFirst,
        tleSecond,
        centerFrequency,
        polarizationKey: polarizationId,
        bitRate,
        modulationKey: modulationId,
        convolutionalCodeKey: convolutionalCodeId,
        reedSolomonCodeKey: rsCodeId,
        subFrequency,
        pcmCodingKey: pcmCodingId,
        frameSyncWord,
        frameSize,
        syn,
        ctl,
        lts,
        slip,
        other,
      };
    }

    const spacecraftRes = await axios.patch(`/api/link/${data?.id}`, params);
    console.log('spacecraftRes', spacecraftRes);
    onSpaceCraftUpdated();
    setMessageArray((prev) => [
      ...prev,
      {
        index: messageArrayCount,
        icon: <InformationCircleOutlineIcon />,
        text: `Registration has been completed`
      }
    ]);
    setTimeout(() => onClickDeleteButton(messageArrayCount), 2000);
    setMessageArrayCount((prev) => prev + 1);

    setData(null);
    setIsEdit(false);
  }

  const closeModal = () => {
    setIsEdit(false);
    setData(null);
  };

  useEffect(() => {
    if (!data) {
      setIsEdit(false);
    }
  }, [data]);

  useEffect(() => {
    if (data && isEdit === true) {
      const isUplink = data.direction === 'UPLINK';

      setTleTitle(data.tleTitle ?? '');
      setTleFirst(data.tleFirst ?? '');
      setTleSecond(data.tleSecond ?? '');
      setBitRate(data.bitRate ?? 0);
      setName(data.name ?? '');
      setPolarization(data.polarizationId ?? registerPolarizationList[0].value);
      setOther(data.other ?? '');

      if (isUplink) {
        const pcmCodeOption = registerUplinkPCMCodeList.find((item) => item.label === data.pcmCode);
        const operatingModeOption = registerOperatingModeList.find(
          (item) => item.label === data.operatingMode
        );

        setPcmCode(pcmCodeOption?.value ?? registerUplinkPCMCodeList[0].value);
        setIdlePattern(data.idlePattern ?? '');
        setIdlePatternLength(data.idlePatternLength ?? 0);
        setPreambleLength(data.preambleLength ?? 0);
        setCarrierFrequencyMhz(data.carrierFrequencyMhz ?? 0);
        setOperatingMode(operatingModeOption?.value ?? registerOperatingModeList[0].value);
        setIfCarrierLevel(data.ifCarrierLevel ?? 0);
        setSweepRate(data.sweepRate ?? 0);
        setSweepRange(data.sweepRange ?? 0);
      } else {
        setCenterFreq(data.centerFrequency ?? 0);
        setModulation(data.modulationId ?? registerModulationList[0].value);
        setReedSolomonCode(data.rsCodeId ?? reedSolomonCodeList[0].value);
        setSubcarrierFrequency(data.subFrequency ?? 0);
        setConvolutionalCode(data.convolutionalCodeId ?? registerConvolutionalCodeList[0].value);
        setPCMCoding(data.pcmCodingId ?? registerPCMCodingList[0].value);
        setFrameSyncWord(data.frameSyncWord ?? '');
        setFrameSize(data.frameSize ?? 0);
        setSyn(data.syn ?? 0);
        setCtl(data.ctl ?? 0);
        setLts(data.lts ?? 0);
        setSlip(data.slip ?? 0);
      }
    }
  }, [isEdit]);

  return (
    <>
      <Modal isOpened={!!data}>
        {/* input */}
        {isEdit && data && (
          <div className="h-fit w-[600px] rounded-2xl bg-white shadow-primary">
            <div className="flex flex-row items-center justify-between p-4">
              <Text size="body1" weight="semibold">{data.name}</Text>
              <button type="button" className="flex h-8 w-8 items-center justify-center" onClick={() => setData(null)}>
                <XOutlineIcon className="h-4 w-4" onClick={handleGoBack} />
              </button>
            </div>
            <ul className="flex max-h-[60vh] flex-col gap-4 overflow-auto px-4 pb-4">
              <li className="border-primary rounded-lg border px-3 py-2">
                <Input
                  label="Name"
                  value={name}
                  setValue={setName}
                />
              </li>
              <li className="border-primary rounded-lg border px-3 py-2">
                <Label
                  label={'NORAD ID'}
                  htmlFor={'NORAD ID'}
                  labelTooltip={'NORAD ID'}
                />
                <Text weight="medium" size="body2" color="primary">{data['noradId']}</Text>
              </li>
              <li className="border-primary rounded-lg border px-3 py-2">
                <Input
                  label="TLE - Title"
                  labelTooltip="TLE - Title"
                  value={tleTitle}
                  setValue={setTleTitle}
                />
              </li>
              <li className="border-primary rounded-lg border px-3 py-2">
                <Input
                  label="TLE - Line 1"
                  labelTooltip="TLE - Line 1"
                  value={tleFirst}
                  setValue={setTleFirst}
                />
              </li>
              <li className="border-primary rounded-lg border px-3 py-2">
                <Input
                  label="TLE - Line 2"
                  labelTooltip="TLE - Line 2"
                  value={tleSecond}
                  setValue={setTleSecond}
                />
              </li>
              <li className="border-primary rounded-lg border px-3 py-2">
                <Label
                  label={'Direction'}
                  htmlFor={'Direction'}
                />
                <Text weight="medium" size="body2" color="primary">{data['direction']}</Text>
              </li>
              <li className="border-primary rounded-lg border px-3 py-2">
                <Label
                  label={'Band'}
                  htmlFor={'Band'}
                />
                <Text weight="medium" size="body2" color="primary">{data['band']}</Text>
              </li>
              {data.direction === 'UPLINK' ? (
                <>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <SelectBox
                      className="max-w-none"
                      bgColor="layer3"
                      label="Polarization"
                      labelTooltip="Polarization"
                      list={registerPolarizationList}
                      value={polarizationId}
                      setValue={setPolarization}
                    />
                  </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <Input
                      label="Bit Rate"
                      labelTooltip="Bit Rate"
                      value={bitRate}
                      setValue={setBitRate}
                      type="number"
                    />
                  </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <SelectBox
                      className="max-w-none"
                      bgColor="layer3"
                      label="PCM Code"
                      list={registerUplinkPCMCodeList}
                      value={pcmCode}
                      setValue={setPcmCode}
                    />
                  </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <div className="flex flex-row">
                      <Label
                        label={'Idle Pattern'}
                        htmlFor={'Idle Pattern'}
                        labelTooltip={'Hexadecimal value (e.g., B669FD2E)'}
                      />
                    </div>
                    <div className="flex flex-row gap-2">
                      <Input
                        className="w-full !flex-row justify-between gap-1"
                        label="0x"
                        value={idlePattern}
                        setValue={setIdlePattern}
                        placeholder="Hexadecimal value (e.g., B669FD2E)"
                      />
                    </div>
                  </li>
                    <li className="border-primary rounded-lg border px-3 py-2">
                      <Input
                        label="Idle Pattern Length"
                        labelTooltip="0 ~ 32"
                        value={idlePatternLength}
                        setValue={setIdlePatternLength}
                        type="number"
                      />
                    </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <Input
                      label="Preamble Length"
                      labelTooltip="0 ~ 16,777,214"
                      value={preambleLength}
                      setValue={setPreambleLength}
                      type="number"
                    />
                  </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <Input
                      label="Carrier Frequency (MHz)"
                      labelTooltip="2025 ~ 2120"
                      value={carrierFrequencyMhz}
                      setValue={setCarrierFrequencyMhz}
                      type="number"
                    />
                  </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <SelectBox
                      className="max-w-none"
                      bgColor="layer3"
                      label="Operating Mode"
                      list={registerOperatingModeList}
                      value={operatingMode}
                      setValue={setOperatingMode}
                    />
                  </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <Input
                      label="IF Carrier Level (dB)"
                      labelTooltip="-80.0 ~ 10.0"
                      value={ifCarrierLevel}
                      setValue={setIfCarrierLevel}
                      type="number"
                    />
                  </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <Input
                      label="Sweep Rate"
                      labelTooltip="0.0001 ~ 175,000.0"
                      value={sweepRate}
                      setValue={setSweepRate}
                      type="number"
                    />
                  </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <Input
                      label="Sweep Range"
                      labelTooltip="1,000 ~ 1,000,000"
                      value={sweepRange}
                      setValue={setSweepRange}
                      type="number"
                    />
                  </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <InputTextArea
                      inputClassName="h-[100px]"
                      label="(Optional) Other"
                      value={other}
                      setValue={setOther}
                    />
                  </li>
                </>
              ) : (
                <>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <Input
                      label="Center Frequency (MHz)"
                      labelTooltip="2200~2300(Rx)"
                      value={centerFrequency}
                      setValue={setCenterFreq}
                      type="number"
                    />
                  </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <SelectBox
                      className="max-w-none"
                      bgColor="layer3"
                      label="Modulation"
                      labelTooltip="Modulation"
                      list={registerModulationList}
                      value={modulationId}
                      setValue={setModulation}
                    />
                  </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <Input
                      label="Bit Rate"
                      labelTooltip="Bit Rate"
                      value={bitRate}
                      setValue={setBitRate}
                      type="number"
                    />
                  </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <SelectBox
                      className="max-w-none"
                      bgColor="layer3"
                      label="Polarization"
                      labelTooltip="Polarization"
                      list={registerPolarizationList}
                      value={polarizationId}
                      setValue={setPolarization}
                    />
                  </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <SelectBox
                      className="max-w-none"
                      bgColor="layer3"
                      label="Convolutional Code"
                      labelTooltip="Convolutional Code"
                      list={registerConvolutionalCodeList}
                      value={convolutionalCodeId}
                      setValue={setConvolutionalCode}
                    />
                  </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <RadioSet
                      label="Reed-Solomon Code(FEC)"
                      list={reedSolomonCodeList}
                      value={rsCodeId}
                      setValue={(value) => setReedSolomonCode(Number(value))}
                    />
                  </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <Input
                      label="Subcarrier Frequency"
                      labelTooltip="High bandwidth with sub-carrier"
                      type="number"
                      value={subFrequency}
                      setValue={setSubcarrierFrequency}
                      placeholder="5,000 to 2,000,000 (in Hz)"
                    />
                  </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <SelectBox
                      className="max-w-none"
                      bgColor="layer3"
                      label="PCM Coding"
                      labelTooltip="PCM Coding"
                      list={registerPCMCodingList}
                      value={pcmCodingId}
                      setValue={setPCMCoding}
                    />
                  </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <div className="flex flex-row">
                      <Label
                        label={'Sync Word'}
                        htmlFor={'Sync Word'}
                        labelTooltip={'Base :  0x1ACFFC1D (32bit, 4byte) '}
                      />
                    </div>
                    <div className="flex flex-row gap-2">
                      <Input
                        className="w-full !flex-row justify-between gap-1"
                        label="0x"
                        value={frameSyncWord}
                        setValue={setFrameSyncWord}
                        placeholder="1ACFFC1D (32bit, 4byte)"
                      />
                    </div>
                  </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <Input
                      label="Frame Size"
                      labelTooltip="10 ~ 32000 in bytes"
                      type="number"
                      value={frameSize}
                      setValue={setFrameSize}
                      placeholder="10 ~ 32000 in bytes"
                    />
                  </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <div className="flex flex-col gap-2">
                      <Label
                        label={'Synchronization Strategy'}
                        htmlFor={'Synchronization Strategy'}
                        labelTooltip={'Synchronization Strategy'}
                      />
                      <div className="flex gap-2">
                        <Input label="SYN" labelTooltip="SYN" value={syn} setValue={setSyn} type={'number'} />
                        <Input label="CTL" labelTooltip="CTL" value={ctl} setValue={setCtl} type={'number'} />
                        <Input label="LTS" labelTooltip="LTS" value={lts} setValue={setLts} type={'number'} />
                        <Input label="SLIP" labelTooltip="SLIP" value={slip} setValue={setSlip} type={'number'} />
                      </div>
                    </div>
                  </li>
                  <li className="border-primary rounded-lg border px-3 py-2">
                    <InputTextArea
                      inputClassName="h-[100px]"
                      label="(Optional) Other"
                      value={other}
                      setValue={setOther}
                    />
                  </li>
                </>
              )}
            </ul>
            <div className="flex w-full flex-row items-center justify-between bg-bg-transparent_gray_light p-4">
            <Button text={t('manage.button.cancel')} onClick={closeModal} type="secondary" size="large" />
            <Button text={t('manage.button.save')} onClick={onSave} type="primary" size="large" />
            </div>
          </div>
        )}
        {/* View */}
        {!isEdit && data && (
          <ModalInner title={data.name} onClickClose={closeModal}>
            <>
              <ul className="flex max-h-[60vh] flex-col gap-4 overflow-auto px-4 pb-4">
                {(data.direction === 'UPLINK' ? manageModalDetailListUplink : manageModalDetailListDownlink).map(
                  ({ title, key }: { title: string; key: string }) => {
                    const value = data?.[key as keyof SpaceCraftDetailData];
                    const displayTitle = title.startsWith('manage.') ? t(title) : title;

                    return (
                      <SpaceCraftModalListElement
                        key={key}
                        title={displayTitle}
                        value={value !== undefined && value !== null ? value : '-'}
                      />
                    );
                  }
                )}
              </ul>
              <div className="flex w-full flex-row items-center justify-between bg-bg-transparent_gray_light p-4">
              <Button text={t('manage.button.delete')} onClick={openDeleteModal} type="secondary" size="large" />
              <Button text={t('manage.button.edit')} onClick={onEdit} type="primary" size="large" />
              </div>
            </>
          </ModalInner>
        )}
      </Modal>
      {isDeleteModalOpened && (
        <DetailDeleteModal
          data={data}
          setData={setData}
          setIsDeleteModalOpened={setIsDeleteModalOpened}
          onDeleted={onSpaceCraftUpdated} />
      )}
      <InfoMessage messageArray={messageArray} onClickDelete={onClickDeleteButton} />
    </>
  );
};

export default SpaceCraftModal;
